import { useEffect, useState } from 'react';
import { PrimaryButton, TextButton } from '@/lib';
import { apiFetch } from '@/app/api-fetch';
import { useApiConfig } from '@/app/ApiProvider';

/**
 * Comment thread + composer for S4 Alert Detail. Authors comments
 * against the alert, supports `@you` and `@<name>` mentions via a
 * lightweight regex pass at post time, and renders prior comments
 * in chronological order.
 *
 * Mentions are detected client-side (regex on `@\S+`) and forwarded
 * to the MSW handler, which echoes them back on the saved comment
 * AND, for `@you`, pushes an Inbox row. The handler is the single
 * source of truth — clients don't synthesize Inbox rows directly.
 *
 * The composer uses plain `apiFetch` rather than the typed alert
 * client because the comments endpoint isn't in the OpenAPI spec
 * yet — adding it requires a contracts edit + SDK regen (see the
 * Phase 6 backlog). The wire shape here will match that endpoint
 * 1:1 so the future swap is a single-file change.
 */
export type AlertComment = {
  id: string;
  alertId: string;
  body: string;
  author: { id: string; displayName: string };
  createdAt: string;
  mentions: string[];
};

export function AlertComments({ alertId }: { alertId: string }) {
  const config = useApiConfig();
  const [items, setItems] = useState<AlertComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch on mount + whenever alertId changes. The list is small
  // (≤ a few dozen comments per alert) so client paging is deferred.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(config, `/v1/alerts/${alertId}/comments`)
      .then((r) => r.json())
      .then((body: { items?: AlertComment[] }) => {
        if (cancelled) return;
        setItems(body.items ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [alertId, config]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    setError(null);
    try {
      const mentions = extractMentions(body);
      const res = await apiFetch(config, `/v1/alerts/${alertId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body,
          mentions,
          author: { id: 'u_local', displayName: 'You' },
        }),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error ?? `posting failed (${res.status})`);
      }
      const saved = (await res.json()) as AlertComment;
      setItems((prev) => [...prev, saved]);
      setDraft('');
    } catch (err) {
      setError(String(err));
    } finally {
      setPosting(false);
    }
  }

  return (
    <section aria-labelledby="comments-heading" className="flex flex-col gap-sp-3">
      <div className="flex items-baseline justify-between">
        <h2
          id="comments-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Comments ({items.length})
        </h2>
        <span className="text-fs-meta text-gray-700">
          Tip: type <span className="font-mono">@you</span> or <span className="font-mono">@&lt;name&gt;</span> to mention.
        </span>
      </div>

      {loading ? (
        <p className="text-fs-meta text-gray-700">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-fs-meta text-gray-700">No comments yet.</p>
      ) : (
        <ul className="flex flex-col gap-sp-2">
          {items.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-sp-1 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
            >
              <div className="flex items-baseline justify-between gap-sp-2">
                <span className="text-fs-meta font-medium text-gray-900">
                  {c.author.displayName}
                </span>
                <time
                  dateTime={c.createdAt}
                  className="font-mono text-fs-meta text-gray-700"
                >
                  {new Date(c.createdAt).toLocaleString()}
                </time>
              </div>
              <p className="text-fs-body text-gray-900">
                {renderWithMentions(c.body)}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-col gap-sp-2">
        <label htmlFor="comment-draft" className="sr-only">
          Add a comment
        </label>
        <textarea
          id="comment-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a comment… use @you or @<name> to mention."
          rows={3}
          className="w-full rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 text-fs-body text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
        />
        <div className="flex items-center justify-end gap-sp-2">
          {error ? (
            <p role="alert" className="mr-auto text-fs-meta text-critical">
              {error}
            </p>
          ) : null}
          {draft ? (
            <TextButton
              label="Cancel"
              onClick={() => {
                setDraft('');
                setError(null);
              }}
            />
          ) : null}
          <PrimaryButton
            label={posting ? 'Posting…' : 'Post comment'}
            disabled={!draft.trim() || posting}
            onClick={() => submit(new Event('submit') as unknown as React.FormEvent)}
          />
        </div>
      </form>
    </section>
  );
}

/**
 * Extract `@you` / `@<name>` tokens from comment text. Mention rules
 * for the demo: anything matching `@` + non-whitespace, case-sensitive
 * on `@you` so a sentence like "thank you" isn't misread as a mention.
 */
function extractMentions(body: string): string[] {
  const out: string[] = [];
  const re = /(^|\s)@([\w.-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) != null) {
    out.push(`@${m[2]}`);
  }
  return out;
}

/**
 * Render a comment body with `@mention` tokens styled. Keeps things
 * simple: split on the regex, wrap mention segments in a span.
 */
function renderWithMentions(body: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /(^|\s)(@[\w.-]+)/g;
  let lastIdx = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) != null) {
    const start = m.index + m[1].length;
    if (start > lastIdx) parts.push(body.slice(lastIdx, start));
    parts.push(
      <span key={key++} className="rounded-r-chip bg-info-bg px-sp-1 font-mono text-fs-meta text-info-fg">
        {m[2]}
      </span>,
    );
    lastIdx = start + m[2].length;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts;
}
